import { useEffect, useState } from "react";
import axios from "axios";
import { logAuditEvent, AuditChange } from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";

const HOST = process.env.REACT_APP_HOST_URL || "";
const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

interface Product {
  id: string;
  name: string;
  label: string;
  product_id: string;
}

interface PromoCode {
  id: string;
  code: string;
  discountType: "percentage" | "fixed";
  discountAmount: number;
  validProducts: string[];
  startDate: string;
  endDate: string;
  status: "active" | "inactive" | "expired";
  createdAt: string;
}

const emptyForm = (): Omit<PromoCode, "id" | "createdAt"> => ({
  code: "",
  discountType: "percentage",
  discountAmount: 0,
  validProducts: [],
  startDate: "",
  endDate: "",
  status: "active",
});

function StatusBadge({ status }: { status: PromoCode["status"] }) {
  const styles: Record<string, { bg: string; color: string; label: string }> = {
    active:   { bg: "rgba(25,135,84,0.1)",  color: "#146c43", label: "Active"   },
    inactive: { bg: "rgba(108,117,125,0.1)", color: "#495057", label: "Inactive" },
    expired:  { bg: "rgba(220,53,69,0.1)",  color: "#b02a37", label: "Expired"  },
  };
  const s = styles[status] ?? styles.inactive;
  return (
    <span style={{
      display: "inline-block", padding: "0.15rem 0.55rem", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 600,
      backgroundColor: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

export default function PromoCodes() {
  const [codes, setCodes]       = useState<PromoCode[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState<PromoCode | null>(null);
  const [form, setForm]         = useState(emptyForm());
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${HOST}/api/admin/promo-codes`),
      axios.get(`${HOST}/api/vip/products`),
    ]).then(([promoResp, prodResp]) => {
      if (promoResp.data.success) setCodes(promoResp.data.codes || []);
      setProducts(Array.isArray(prodResp.data) ? prodResp.data : []);
    }).finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setError("");
    setShowModal(true);
  };

  const openEdit = (p: PromoCode) => {
    setEditing(p);
    setForm({
      code: p.code,
      discountType: p.discountType,
      discountAmount: p.discountAmount,
      validProducts: p.validProducts,
      startDate: p.startDate,
      endDate: p.endDate,
      status: p.status ?? "active",
    });
    setError("");
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const toggleProduct = (productId: string) => {
    setForm((f) => ({
      ...f,
      validProducts: f.validProducts.includes(productId)
        ? f.validProducts.filter((id) => id !== productId)
        : [...f.validProducts, productId],
    }));
  };

  const save = async () => {
    setError("");
    if (!form.code.trim())       return setError("Promo code is required.");
    if (!form.discountAmount || form.discountAmount <= 0) return setError("Discount amount must be greater than 0.");
    if (form.discountType === "percentage" && form.discountAmount > 100) return setError("Percentage cannot exceed 100.");
    if (!form.startDate)         return setError("Start date is required.");
    if (!form.endDate)           return setError("End date is required.");
    if (form.startDate > form.endDate) return setError("End date must be after start date.");

    setSaving(true);
    try {
      const discountSummary = form.discountType === "percentage"
        ? `${form.discountAmount}% off`
        : `$${form.discountAmount.toFixed(2)} off`;
      const statusLabel = { active: "Active", inactive: "Inactive", expired: "Expired" }[form.status] ?? form.status;
      if (editing) {
        const { data } = await axios.put(`${HOST}/api/admin/promo-codes/${editing.id}`, form);
        if (!data.success) return setError(data.message || "Failed to update.");
        setCodes((prev) => prev.map((c) => c.id === editing.id ? data.code : c));
        const promoChanges: AuditChange[] = [
          editing.code           !== form.code           ? { field: "Code",       oldValue: editing.code,                  newValue: form.code }                  : null,
          editing.discountType   !== form.discountType   ? { field: "Type",       oldValue: editing.discountType,          newValue: form.discountType }          : null,
          editing.discountAmount !== form.discountAmount ? { field: "Discount",   oldValue: String(editing.discountAmount), newValue: String(form.discountAmount) } : null,
          (editing.status ?? "active") !== form.status   ? { field: "Status",     oldValue: editing.status ?? "active",    newValue: form.status }                : null,
          editing.startDate      !== form.startDate      ? { field: "Start Date", oldValue: editing.startDate,             newValue: form.startDate }             : null,
          editing.endDate        !== form.endDate        ? { field: "End Date",   oldValue: editing.endDate,               newValue: form.endDate }               : null,
        ].filter((c): c is AuditChange => c !== null);
        logAuditEvent({
          actor:   getAdminSession()?.displayName || "Admin",
          action:  "promo_code",
          label:   "Promo Code Updated",
          details: `${form.code} — ${discountSummary}, ${statusLabel}`,
          changes: promoChanges.length > 0 ? promoChanges : undefined,
        });
      } else {
        const { data } = await axios.post(`${HOST}/api/admin/promo-codes`, form);
        if (!data.success) return setError(data.message || "Failed to create.");
        setCodes((prev) => [...prev, data.code]);
        logAuditEvent({
          actor:   getAdminSession()?.displayName || "Admin",
          action:  "promo_code",
          label:   "Promo Code Created",
          details: `${form.code} — ${discountSummary}, ${statusLabel} (${form.startDate} to ${form.endDate})`,
        });
      }
      closeModal();
    } catch (err: any) {
      setError(err?.response?.data?.message || "An error occurred.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCode = async (id: string) => {
    if (!window.confirm("Delete this promo code?")) return;
    setDeleting(id);
    try {
      const codeToDelete = codes.find((c) => c.id === id);
      await axios.delete(`${HOST}/api/admin/promo-codes/${id}`);
      setCodes((prev) => prev.filter((c) => c.id !== id));
      if (codeToDelete) {
        logAuditEvent({
          actor:   getAdminSession()?.displayName || "Admin",
          action:  "promo_code",
          label:   "Promo Code Deleted",
          details: codeToDelete.code,
        });
      }
    } finally {
      setDeleting(null);
    }
  };

  const sorted = [...codes].sort((a, b) => {
    const order: Record<string, number> = { active: 0, inactive: 1, expired: 2 };
    const sa = order[a.status ?? "inactive"] ?? 1;
    const sb = order[b.status ?? "inactive"] ?? 1;
    return sa - sb || a.code.localeCompare(b.code);
  });

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 style={{ color: BRAND_GREEN, fontWeight: 700, margin: 0 }}>Promo Codes</h4>
          <p className="text-muted small mb-0 mt-1">Manage discount codes for VIP membership packages.</p>
        </div>
        <button
          className="btn btn-sm"
          style={{ backgroundColor: BRAND_GREEN, color: "#fff", borderRadius: 8, fontWeight: 600, fontSize: "0.85rem", padding: "0.45rem 1rem" }}
          onClick={openCreate}
        >
          <i className="bi bi-plus-lg me-1" />
          Create Promo Code
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : sorted.length === 0 ? (
        <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "3rem", textAlign: "center" }}>
          <i className="bi bi-tag" style={{ fontSize: "2rem", color: "#adb5bd" }} />
          <p className="text-muted mt-2 mb-0">No promo codes yet. Create one to get started.</p>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden" }}>
          <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th className="px-3 py-2 text-uppercase small text-muted fw-semibold">Code</th>
                <th className="px-3 py-2 text-uppercase small text-muted fw-semibold">Discount</th>
                <th className="px-3 py-2 text-uppercase small text-muted fw-semibold">Valid For</th>
                <th className="px-3 py-2 text-uppercase small text-muted fw-semibold">Start Date</th>
                <th className="px-3 py-2 text-uppercase small text-muted fw-semibold">End Date</th>
                <th className="px-3 py-2 text-uppercase small text-muted fw-semibold">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const status = p.status ?? "inactive";
                const validFor = p.validProducts.length === 0
                  ? "All packages"
                  : p.validProducts.map((id) => products.find((pr) => pr.product_id === id)?.label || id).join(", ");
                return (
                  <tr key={p.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td className="px-3 py-2 fw-semibold" style={{ fontFamily: "monospace", letterSpacing: "0.05em" }}>
                      {p.code}
                    </td>
                    <td className="px-3 py-2">
                      {p.discountType === "percentage"
                        ? `${p.discountAmount}% off`
                        : `$${p.discountAmount.toFixed(2)} off`}
                    </td>
                    <td className="px-3 py-2 text-muted" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {validFor}
                    </td>
                    <td className="px-3 py-2 text-muted">{p.startDate}</td>
                    <td className="px-3 py-2 text-muted">{p.endDate}</td>
                    <td className="px-3 py-2"><StatusBadge status={status} /></td>
                    <td className="px-3 py-2 text-nowrap">
                      <div className="d-flex gap-1 justify-content-end">
                        <button
                          className="btn btn-sm"
                          title="Edit"
                          style={{ backgroundColor: "rgba(2,56,34,0.08)", border: "none", color: BRAND_GREEN, fontSize: "0.8rem", padding: "0.2rem 0.5rem", borderRadius: 6 }}
                          onClick={() => openEdit(p)}
                        >
                          <i className="bi bi-pencil-fill" />
                        </button>
                        <button
                          className="btn btn-sm"
                          title="Delete"
                          style={{ backgroundColor: "rgba(220,53,69,0.08)", border: "none", color: "#b02a37", fontSize: "0.8rem", padding: "0.2rem 0.5rem", borderRadius: 6 }}
                          disabled={deleting === p.id}
                          onClick={() => deleteCode(p.id)}
                        >
                          <i className="bi bi-trash-fill" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", position: "fixed", inset: 0, zIndex: 1050, overflowY: "auto" }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 520, margin: "2rem auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ borderRadius: 14, border: "none", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
              {/* Modal header */}
              <div className="modal-header" style={{ backgroundColor: BRAND_GREEN, borderRadius: "14px 14px 0 0", padding: "1rem 1.25rem" }}>
                <h6 className="modal-title mb-0 fw-bold" style={{ color: BRAND_GOLD, letterSpacing: "0.04em" }}>
                  {editing ? "Edit Promo Code" : "Create Promo Code"}
                </h6>
                <button type="button" className="btn-close btn-close-white" onClick={closeModal} />
              </div>

              {/* Modal body */}
              <div className="modal-body" style={{ padding: "1.5rem" }}>
                {error && (
                  <div className="alert alert-danger py-2 small mb-3">{error}</div>
                )}

                {/* Code */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Promo Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. SUMMER20"
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                    style={{ fontFamily: "monospace", letterSpacing: "0.05em", textTransform: "uppercase" }}
                  />
                </div>

                {/* Discount type + amount */}
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold">Discount Type <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={form.discountType}
                      onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value as "percentage" | "fixed" }))}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold">Amount <span className="text-danger">*</span></label>
                    <div className="input-group">
                      {form.discountType === "fixed" && <span className="input-group-text">$</span>}
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        max={form.discountType === "percentage" ? 100 : undefined}
                        step={form.discountType === "percentage" ? 1 : 0.01}
                        value={form.discountAmount || ""}
                        onChange={(e) => setForm((f) => ({ ...f, discountAmount: Number(e.target.value) }))}
                      />
                      {form.discountType === "percentage" && <span className="input-group-text">%</span>}
                    </div>
                  </div>
                </div>

                {/* Valid for */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Valid For</label>
                  <p className="text-muted small mb-2">Leave all unchecked to apply to all packages.</p>
                  <div style={{ border: "1px solid #dee2e6", borderRadius: 8, padding: "0.75rem", maxHeight: 180, overflowY: "auto" }}>
                    {products.length === 0 ? (
                      <span className="text-muted small">Loading products…</span>
                    ) : (
                      products.map((prod) => (
                        <div key={prod.product_id} className="form-check mb-1">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`prod-${prod.product_id}`}
                            checked={form.validProducts.includes(prod.product_id)}
                            onChange={() => toggleProduct(prod.product_id)}
                          />
                          <label className="form-check-label small" htmlFor={`prod-${prod.product_id}`}>
                            {prod.label || prod.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Status <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as PromoCode["status"] }))}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                  </select>
                  <div className="form-text">
                    Only <strong>Active</strong> codes can be applied at checkout.
                  </div>
                </div>

                {/* Dates */}
                <div className="row mb-1">
                  <div className="col-6">
                    <label className="form-label small fw-semibold">Start Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.startDate}
                      onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold">End Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.endDate}
                      min={form.startDate}
                      onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="modal-footer" style={{ borderTop: "1px solid #e9ecef", padding: "0.85rem 1.25rem" }}>
                <button className="btn btn-sm btn-light" onClick={closeModal} disabled={saving}>Cancel</button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: BRAND_GREEN, color: "#fff", fontWeight: 600, minWidth: 100 }}
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : editing ? "Save Changes" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
